// @ts-check
import {
    CalendarOutlined,
    DownloadOutlined,
    LoadingOutlined,
    PlusCircleOutlined,
    ReloadOutlined,
    TableOutlined
} from "@ant-design/icons";
import { Tabs } from "antd";
import SyncInProgressSpinner from '~/components/Offline/SyncInProgressSpinner';
import { BorderedPanel } from '~/components/Shared/PageParts';
import { CachedApiLoaderProvider } from '~/components/Util/CachedApiLoader';
import { useZeroContext } from '~/components/ZeroContext';
import { useEffect, useState } from "react";
import { export_courses_analytics, export_form_analytics, get_form_drafts } from '~/api/zero-api';
import "~/assets/css/antd-custom.css";
import {
    floatingButtonMargin,
    getParamFromLocation,
    isAdmin,
    isAdminOrTeamLead,
    isMobileApp,
    isViewer,
    mainContentContainerClass,
    scrollToTop,
    setParamDefault,
    sleep,
    updateHistoryState
} from '~/other/Helper';
import NotificationAlert from '~/other/NotificationAlert';
import {
    updateFormsFilterQuery,
    updateFormsPagePosition,
    updateFormsPagination,
    updateFormsPeriodQuery,
    updateFormsTab,
} from '~/store/actions/FormsActions';
import GenerateReport from '~/components/AdminPages/Reports/GenerateReport';
import InfoIconComponent from '~/components/InfoIconComponent';
import ButtonLoading from '~/components/Shared/ButtonLoading';
import FormAnalyticsTab from '~/components/Forms/FormAnalyticsTab';
import MyDraftsModal from '~/components/Forms/MyDraftsModal';

import FormSettings from '~/components/AdminPages/AccountSettings/FormSettings';
import SubmissionSyncErrorAlertList from '~/components/Offline/Forms/SubmissionSyncErrorAlertList';
import Button from '~/components/Shared/Button';
import { useCurrentUser } from '~/hooks/reduxHooks';
import useOrganizationId from '~/hooks/useOrganizationId';
import useZeroSelector from '~/hooks/useZeroSelector';
import useHistoryStateStore from '~/hooks/useHistoryStateStore';
import { useDispatch } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { INITIAL_FORMS_PERIOD_QUERY } from '~/store/helpers/defaults';
import "~/assets/css/antd-custom.css";
import { AssignmentDataGridContextProvider } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.context';
import AssignmentDataGrid from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid';
import { EventNames as AssignmentGridEvent } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.utils';
import AssignmentDataGridExportButton from '~/components/Forms/AssignmentDataGrid/AssignmentDataGridExportButton';
import ResetAssignmentGridLink from '~/components/Forms/AssignmentDataGrid/ResetAssignmentGridLink';
import FormsPageFilters from '~/components/Forms/FormsPageFilters';
import ScheduleCalendar from '~/components/Forms/ScheduleCalendar';
import ResetScheduleGridLink from '~/components/Forms/ScheduleDataGrid/ResetScheduleGridLink';
import { ScheduleDataGridContextProvider } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.context';
import ScheduleDataGrid from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid';
import { EventNames as ScheduleGridEvent } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.utils';
import ScheduleDataGridExportButton from '~/components/Forms/ScheduleDataGrid/ScheduleDataGridExportButton';
import SelectFormModal from '~/components/Forms/SelectFormModal';
import ResetSubmissionGridLink from '~/components/Forms/SubmissionDataGrid/ResetSubmissionGridLink';
import { SubmissionDataGridContextProvider } from '~/components/Forms/SubmissionDataGrid/SubmissionDataGrid.context';
import SubmissionDataGrid from '~/components/Forms/SubmissionDataGrid/SubmissionDataGrid';
import { SubmissionDataGridModals } from '~/components/Forms/SubmissionDataGrid/SubmissionDataGrid.modals';
import { EventNames as SubmissionGridEvent } from '~/components/Forms/SubmissionDataGrid/SubmissionDataGrid.utils';
import SubmissionDataGridExportButton from '~/components/Forms/SubmissionDataGrid/SubmissionDataGridExportButton';
import ResetTemplateGridLink from '~/components/Forms/TemplateDataGrid/ResetTemplateGridLink';
import { TemplateDataGridContextProvider } from '~/components/Forms/TemplateDataGrid/TemplateDataGrid.context';
import TemplateDataGrid from '~/components/Forms/TemplateDataGrid/TemplateDataGrid';
import { EventNames as TemplateGridEvent } from '~/components/Forms/TemplateDataGrid/TemplateDataGrid.utils';
import TemplateDataGridExportButton from '~/components/Forms/TemplateDataGrid/TemplateDataGridExportButton';

/** @returns {("select-form" | "drafts" | "create-template" | "create-schedule" | null)} */
function getInitialModal(location) {
    const params = new URLSearchParams(location.search);
    const modal = params.get("modal");
    switch (modal) {
        case "select-form":
            return "select-form";
        default:
            return null;
    }
}

export default function FormsPage() {
    const context = useZeroContext();
    const location = useLocation();
    const history = useHistory();
    const { formType, tab: tabFromParams } = useParams();
    const dispatch = useDispatch();

    const user = useCurrentUser();
    const orgId = useOrganizationId();
    const currentTeamId = useZeroSelector((state) => state.teams_helper.team.uuid ?? "");
    const teams = useZeroSelector((state) => state.teams_helper.teams ?? []);
    const formsQuery = useZeroSelector((state) => state.forms.query ?? "");
    const periodQuery = useZeroSelector((state) => state.forms.period_query ?? "");
    const pagination = useZeroSelector((state) => state.forms.pagination ?? 1);

    const [drafts, setDrafts] = useState(null);
    /** @type {useState<'select-form' | 'drafts' | 'create-template' | 'create-schedule' | null>} */
    const [currentModal, setCurrentModal] = useState(getInitialModal(location));
    const [isExportingAnalytics, setIsExportingAnalytics] = useState(false);
    const [showCalendarViewForScheduleTab, setShowCalendarViewForScheduleTab] = useState(
        getParamFromLocation("tabview") === "calendar"
    );
    const [loading, setLoading] = useState(true);
    const [analyticsRefreshCounter, setAnalyticsRefreshCounter] = useState(0);

    /** @type {FormsPageTab} */
    const activeTab = tabFromParams ?? "submissions";
    /** @type {FormsPageType} */
    const pageType = formType === "courses" ? "lms" : "regular";
    const isAdminOrLead = isAdminOrTeamLead(user);

    useHistoryStateStore(() => ({
        formsQuery,
        periodQuery,
    }), [formsQuery, periodQuery]);

    const fetchDrafts = async () => {
        const query = `?form_types=${pageType}`;
        try {
            let { drafts } = await getCachedDrafts(query);
            drafts = drafts.sort((a, b) => (a.edited_at < b.edited_at ? 1 : -1));
            setDrafts(drafts);
        } catch (err) {
            NotificationAlert("error", "", "Unable to load your drafts.");
        }
    };

    useEffect(() => {
        dispatch(updateFormsTab(activeTab));

        if (activeTab === "analytics") {
            const query = location.search;
            if (query.includes("export=true")) {
                const [filterQuery, dateQuery] = splitExportQuery(query);
                if (filterQuery) {
                    dispatch(updateFormsFilterQuery(filterQuery));
                }
                if (dateQuery) {
                    dispatch(updateFormsPeriodQuery(dateQuery));
                }
            }
        }

        if (activeTab === "schedules") {
            const params = new URLSearchParams(formsQuery);
            if (setParamDefault(params, "schedule_status", "active")) {
                dispatch(updateFormsFilterQuery(`?${params}`));
            }
        }

        fetchDrafts();

        setTimeout(() => setLoading(false), 0); // Wait one event loop before displaying any content

        return () => {
            dispatch(updateFormsPagePosition(window.pageYOffset));
            dispatch(updateFormsPagination(pagination));
        };
    }, []);

    useEffect(() => {
        // reset filters when pageType changes
        if (location.state?.formsQuery) {
            dispatch(updateFormsFilterQuery(location.state.formsQuery));
        } else {
            const teamUuid = isAdmin(user) ? "organization_teams" : "my_teams";
            dispatch(updateFormsFilterQuery(`?team_uuid=${teamUuid}`));
        }

        if (location.state?.periodQuery) {
            dispatch(updateFormsPeriodQuery(location.state.periodQuery));
        } else {
            dispatch(updateFormsPeriodQuery(INITIAL_FORMS_PERIOD_QUERY));
        }
    }, [pageType, location.state]);

    useEffect(() => {
        scrollToTop("page-head");
        dispatch(updateFormsTab(activeTab));
        dispatch(updateFormsPagination(1));
        dispatch(updateFormsPagePosition(0));
        fetchDrafts();
    }, [location.pathname]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (activeTab === "schedules") {
            params.set("tabview", showCalendarViewForScheduleTab ? "calendar" : "table");
            updateHistoryState(history, {gridState: location.state?.gridState});
        } else if (params.has("tabview")) {
            params.delete("tabview");
            updateHistoryState(history, {gridState: location.state?.gridState});
        }
    }, [activeTab, showCalendarViewForScheduleTab]);

    const goToSubmitForm = (teamId, formId) => {
        setCurrentModal(null);
        history.push(`/${orgId}/home/team/${teamId}/${pageType === "lms" ? "courses" : "forms"}/${formId}/submit`);
    };

    const editDraft = (draft) => {
        history.push(
            `/${orgId}/home/team/${draft.team.uuid}/${pageType === "regular" ? "forms" : "courses"}/${
                draft.form.form_uuid
            }/submission/${draft.submission_uuid}`
        );
    };

    const getMyDraftsCount = () => {
        const draftCountFromState = drafts?.length ?? 0;
        if (pageType === "regular") {
            return context.caches.submissionDrafts?.regularDrafts?.length ?? draftCountFromState;
        }
        return draftCountFromState;
    };

    const reloadSubmissions = () => {
        context.events.emit(SubmissionGridEvent.REFRESH_DATA);
    };

    const updateTab = (newTab) => {
        if (newTab === activeTab) {
            return;
        }

        let newPath = "";

        if (tabFromParams === undefined) {
            newPath = `${location.pathname}/${newTab}`;
        } else {
            const pathSegments = location.pathname.split("/").slice(0, -1);
            pathSegments.push(newTab);
            newPath = pathSegments.join("/");
        }

        const params = new URLSearchParams(location.search);
        if (newTab === "schedules") {
            setParamDefault(params, "schedule_status", "active");
        } else {
            params.delete("schedule_status");
        }
        params.delete("form_uuid");

        history.push(newPath + `?${params}`);
    };

    const exportAnalytics = async () => {
        setIsExportingAnalytics(true);

        let query = formsQuery + periodQuery;

        if (query.length > 0 && query[0] === "&") {
            query = "?" + query.substring(1, query.length);
        } else if (query.length > 0 && query[0] !== "?") {
            query = "?" + query;
        }

        const body = JSON.stringify({ query });

        if (window.location.hostname.includes("localhost")) {
            await sleep(2000);
            console.log("export analytics body", body);
        }

        let exportAnalyticsApiFn = pageType === "regular" ? export_form_analytics : export_courses_analytics;

        try {
            const res = await exportAnalyticsApiFn(body);
            const content = await res.json();
            if (content.data.public_url !== null) {
                window.location = content.data.public_url;
            } else {
                // only happens when running against a local running back-end
                console.log("export query", content.data.query);
            }
        } catch (err) {
            NotificationAlert("error", "", `Could not export ${pageType === "regular" ? "form" : "course"} analytics.`);
        } finally {
            setIsExportingAnalytics(false);
        }
    };

    const renderTabActions = (isInline) => {
        const className = isInline ? "hidden-xs" : "visible-xs";
        return (
            <div className={className}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    {activeTab === "submissions" && (
                        <div className="flex gap-md mar-rgt-10">
                            <ResetSubmissionGridLink />
                            <SubmissionDataGridExportButton />
                            <ReloadOutlined
                                className="hover-cursor zero-blue"
                                style={{ fontSize: "18px" }}
                                onClick={() => reloadSubmissions()}
                            />
                        </div>
                    )}
                    {activeTab === "assignments" && (
                        <div className="flex gap-md mar-rgt-10">
                            <ResetAssignmentGridLink />
                            <AssignmentDataGridExportButton />
                            <ReloadOutlined
                                className="hover-cursor zero-blue"
                                style={{ fontSize: "18px" }}
                                onClick={() => context.events.emit(AssignmentGridEvent.REFRESH_DATA)}
                            />
                        </div>
                    )}
                    {activeTab === "analytics" && (
                        <div className="flex gap-md mar-rgt-10">
                            {isExportingAnalytics ? (
                                <LoadingOutlined
                                    className="zero-blue dont-print"
                                    style={{ fontSize: "18px" }}
                                />
                            ) : (
                                <DownloadOutlined
                                    className="hover-cursor zero-blue dont-print"
                                    style={{ fontSize: "20px" }}
                                    onClick={exportAnalytics}
                                />
                            )}
                            <ReloadOutlined
                                className="hover-cursor zero-blue"
                                style={{ fontSize: "18px" }}
                                onClick={() => setAnalyticsRefreshCounter(analyticsRefreshCounter + 1)}
                            />
                        </div>
                    )}
                    {activeTab === "templates" && (
                        <div className="flex gap-md mar-rgt-10">
                            <ResetTemplateGridLink />
                            <TemplateDataGridExportButton />
                            <ReloadOutlined
                                className="hover-cursor zero-blue"
                                style={{ fontSize: "18px" }}
                                onClick={() => context.events.emit(TemplateGridEvent.REFRESH_DATA)}
                            />
                        </div>
                    )}
                    {activeTab === "schedules" && (
                        <>
                            {!showCalendarViewForScheduleTab && (
                                <div className="flex gap-md mar-rgt-10">
                                    <ResetScheduleGridLink />
                                    <ScheduleDataGridExportButton />
                                </div>
                            )}
                            {showCalendarViewForScheduleTab ? (
                                <TableOutlined
                                    className="zero-blue hover-cursor"
                                    style={{ fontSize: "19px" }}
                                    onClick={() => setShowCalendarViewForScheduleTab(false)}
                                />
                            ) : (
                                <CalendarOutlined
                                    className="zero-blue hover-cursor"
                                    style={{ fontSize: "19px" }}
                                    onClick={() => setShowCalendarViewForScheduleTab(true)}
                                />
                            )}
                            <ReloadOutlined
                                className="hover-cursor zero-blue mar-rgt-10 mar-lft-10"
                                style={{ fontSize: "18px" }}
                                onClick={() => context.events.emit(ScheduleGridEvent.REFRESH_DATA)}
                            />
                        </>
                    )}
                    {activeTab === "reports" && (
                        <>
                            <ReloadOutlined
                                className="hover-cursor zero-blue"
                                style={{ fontSize: "18px" }}
                                onClick={() => context.events.emit("refresh-report-history")}
                            />
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return null;
    }

    return (
        <div className={mainContentContainerClass() + " print-7in"}>
            <PageContexts>
                {currentModal === "select-form" && (
                    <SelectFormModal
                        show
                        cancel={() => setCurrentModal(null)}
                        current_team_uuid={currentTeamId}
                        confirm={goToSubmitForm}
                        teams={teams}
                        page_type={pageType}
                        location={location.pathname}
                    />
                )}
                {currentModal === "drafts" && (
                    <MyDraftsModal
                        show
                        cancel={() => {
                            fetchDrafts();
                            setCurrentModal(null);
                        }}
                        editDraft={editDraft}
                        drafts={drafts}
                        pageType={pageType}
                    />
                )}

                <SubmissionDataGridModals />

                <div id="page-head" className="no-padding-mobile no-padding-print">
                    <div className="row print-no-x-margins">
                        <div className="col-lg-12  col-xs-12 col-xs-offset-0 no-padding-print">
                            <div id="page-title" style={{ padding: "0px" }}>
                                <div className="panel thin-border" style={{ margin: "0 auto" }}>
                                    <div className="nav-header-panel no-padding-print">
                                        <div
                                            className="team-name-header flex"
                                            style={{ justifyContent: "space-between", height: 33 }}
                                        >
                                            <h3
                                                className={"section-titles admin-page-header"}
                                                style={{ display: "inline-block", margin: "0px" }}
                                            >
                                                {pageType === "regular" ? "Forms" : "Courses"}
                                                {pageType === "regular" && (
                                                    <InfoIconComponent
                                                        position={"bottom"}
                                                        width={"170px"}
                                                        text={
                                                            <span>
                                                                Anyone can submit a form, but only Admins and Team Leads
                                                                can view all submissions.
                                                            </span>
                                                        }
                                                    />
                                                )}
                                            </h3>
                                            <div className="flex flex-align-center gap-md">
                                                {activeTab === "submissions" && getMyDraftsCount() > 0 && (
                                                    <span
                                                        className="blue-link dont-print"
                                                        style={{ whiteSpace: "nowrap" }}
                                                        onClick={() => setCurrentModal("drafts")}
                                                    >
                                                        My Drafts ({getMyDraftsCount()})
                                                    </span>
                                                )}
                                                <div style={{ height: 33 }}></div>
                                                <FormsPageCreateButton
                                                    activeTab={activeTab}
                                                    pageType={pageType}
                                                    setCurrentModal={setCurrentModal}
                                                />
                                            </div>
                                        </div>
                                        <p
                                            className="header"
                                            style={{
                                                color: "#505050",
                                                marginBottom: "0px",
                                                paddingLeft: "2px",
                                                paddingTop: "0px",
                                            }}
                                        >
                                            {pageType === "regular"
                                                ? "Audits, checklists, inspections, and more."
                                                : "Training, education, learning and more."}
                                        </p>
                                        <div
                                            className="members-search"
                                            style={{
                                                width: "100%",
                                            }}
                                        >
                                            <FormsPageFilters pageType={pageType} activeTab={activeTab} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="page-content" className="no-padding-mobile">
                    {pageType === "regular" && (
                        <SyncInProgressSpinner
                            style={{ padding: "0 0 1rem 1rem" }}
                            postSyncCallback={() => reloadSubmissions()}
                        />
                    )}
                    {pageType === "regular" && activeTab === "submissions" && <SubmissionSyncErrorAlertList />}
                    <BorderedPanel style={{ padding: "0 1.5rem 1.5rem" }}>
                        <Tabs
                            destroyInactiveTabPane={true}
                            activeKey={activeTab}
                            onChange={updateTab}
                            tabBarExtraContent={renderTabActions(true)}
                            renderTabBar={(props, DefaultTabBar) => (
                                <>
                                    <DefaultTabBar {...props} />
                                    {renderTabActions(false)}
                                </>
                            )}
                            items={[
                                {
                                    key: "submissions",
                                    label: "Submissions",
                                    children: (
                                        <div id="tab-content">
                                            <SubmissionDataGrid formType={pageType} />
                                        </div>
                                    ),
                                },
                                {
                                    key: "analytics",
                                    label: "Analytics",
                                    children: (
                                        <div>
                                            <FormAnalyticsTab page_type={pageType} refreshCounter={analyticsRefreshCounter} />
                                        </div>
                                    ),
                                },
                                {
                                    key: "assignments",
                                    label: "Assignments",
                                    children: (
                                        <div id="tab-content">
                                            <AssignmentDataGrid pageType={pageType} />
                                        </div>
                                    ),
                                },
                                !isViewer(user) && {
                                    key: "reports",
                                    label: "Reports",
                                    children: (
                                        <div>
                                            <GenerateReport
                                                isIncidentsReport={false}
                                                isFormsReport={pageType === "regular"}
                                                isCoursesReport={pageType === "lms"}
                                                page_type={pageType}
                                            />
                                        </div>
                                    ),
                                },
                                isAdminOrLead && {
                                    key: "templates",
                                    label: "Templates",
                                    children: (
                                        <div id="tab-content">
                                            <TemplateDataGrid
                                                pageType={pageType}
                                                showCreateModal={currentModal === "create-template"}
                                                onCloseCreateModal={() => setCurrentModal(null)}
                                            />
                                        </div>
                                    ),
                                },
                                isAdminOrLead && {
                                    key: "schedules",
                                    label: "Schedules",
                                    children: (
                                        <div id="tab-content">
                                            {showCalendarViewForScheduleTab ? (
                                                <ScheduleCalendar pageType={pageType} />
                                            ) : (
                                                <ScheduleDataGrid
                                                    pageType={pageType}
                                                    showCreateModal={currentModal === "create-schedule"}
                                                    onCloseCreateModal={() => setCurrentModal(null)}
                                                />
                                            )}
                                        </div>
                                    ),
                                },
                                isAdmin(user) &&
                                    pageType === "regular" && {
                                        key: "settings",
                                        label: "Settings",
                                        children: (
                                            <div>
                                                <FormSettings />
                                            </div>
                                        ),
                                    },
                            ]}
                        />
                    </BorderedPanel>
                </div>

                <FormsPageCreateButton
                    isFloatingButton
                    activeTab={activeTab}
                    pageType={pageType}
                    setCurrentModal={setCurrentModal}
                />
            </PageContexts>
        </div>
    );
}

/**
 * @param {string} query
 * @returns {[string, string]}
 */
function splitExportQuery(query) {
    let filters = query
        .substring(1)
        .split("&")
        .filter((n) => !!n);

    const dateFilters = ["period", "year", "quarter", "month", "from_date", "to_date"];
    let dateQuery = "&";
    let filterQuery = "?";

    for (const i in filters) {
        const filter = filters[i].split("=");
        const filter_type = filter[0];
        const filter_value = filter[1] || "";

        if (dateFilters.includes(filter_type)) {
            dateQuery += `${filter_type}=${filter_value}&`;
        } else {
            filterQuery += `${filter_type}=${filter_value}&`;
        }
    }

    // remove trailing '&' and leading '?'
    dateQuery = dateQuery.substring(0, dateQuery.length - 1);
    // filterQuery = filterQuery.substring(1, filterQuery.length - 1);

    return [filterQuery, dateQuery];
}

let cachedQuery, cachedResult;
async function getCachedDrafts(query) {
    if (query !== cachedQuery) {
        const response = await get_form_drafts(query);
        const content = await response.json();
        cachedQuery = query;
        cachedResult = content;
    }

    return cachedResult;
}

/*
<button
    className="btn btn-primary pull-right dont-print"
    style={{
        textAlign: "center",
        display: "inline-block",
    }}
    onClick={() => {
        setCurrentModal("select-form");
    }}
>
    <span>Submit</span>
</button>
*/

function FormsPageCreateButton({ setCurrentModal, activeTab, pageType, isFloatingButton = false }) {
    const user = useCurrentUser();

    if (isViewer(user) || (isFloatingButton && !isMobileApp())) {
        return null;
    }

    let buttonLabel;
    let modal;

    if (activeTab === "submissions") {
        buttonLabel = "Submit";
        modal = "select-form";
    } else if (activeTab === "templates") {
        buttonLabel = pageType === "lms" ? "Create Course" : "Create Template";
        modal = "create-template";
    } else if (activeTab === "schedules") {
        buttonLabel = "Create Schedule";
        modal = "create-schedule";
    }

    if (!modal) {
        return null;
    }

    if (isFloatingButton) {
        return (
            <div id="floating-button" style={{ bottom: floatingButtonMargin() }} onClick={() => setCurrentModal(modal)}>
                <p className="plus-sign">+</p>
            </div>
        );
    }

    return (
        <Button type="primary" onClick={() => setCurrentModal(modal)}>
            <span className="hidden-xs">{buttonLabel}</span>
            <span className="visible-xs">
                <PlusCircleOutlined />
            </span>
        </Button>
    );
}

/**
 * @param {{
 *  children: React.ReactNode,
 * }} props
 */
function PageContexts({ children }) {
    return (
        <CachedApiLoaderProvider>
            <AssignmentDataGridContextProvider>
                <SubmissionDataGridContextProvider>
                    <TemplateDataGridContextProvider>
                        <ScheduleDataGridContextProvider>{children}</ScheduleDataGridContextProvider>
                    </TemplateDataGridContextProvider>
                </SubmissionDataGridContextProvider>
            </AssignmentDataGridContextProvider>
        </CachedApiLoaderProvider>
    );
}
